import React, { useState, useEffect, useMemo } from "react";
import {
  Modal, Button, Checkbox, InputNumber, Select, Input, Alert, Tabs, Spin, Tag, Avatar, Tooltip,
  message as antMessage,
} from "antd";
import {
  SendOutlined, CheckCircleOutlined, CloseCircleOutlined,
  ShopOutlined, UserOutlined, QuestionCircleOutlined, ReloadOutlined, WarningOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import { API_DOMAIN } from "../constants";
import { _fetchQuickReplies, _fetchSRMUsers, substituteQRText, loadConditionLabels, useQv4Theme } from "./helpers";
import { gpFromSp, QR_IDS, injectPartsTemplate } from "./PriceUpdateDrawer";
import { OverviewTab } from "./WorkflowTaskDrawer";

dayjs.extend(relativeTime);
dayjs.extend(utc);

export function QuoteApprovalModal({ open, onClose, approval, authState, userId, onDone }) {
  const { t, isDark } = useQv4Theme();
  const headers = useMemo(() => ({ accessToken: localStorage.getItem("accessToken") }), []);
  const payload = approval?.payload ?? {};

  const [partSp, setPartSp] = useState({});
  const [partVat, setPartVat] = useState({});
  const [deliveryEnabled, setDeliveryEnabled] = useState(true);
  const [deliveryAmount, setDeliveryAmount] = useState(0);
  const [includedPartIds, setIncludedPartIds] = useState(new Set());
  const [activeTab, setActiveTab] = useState("b2c");
  const [messageText, setMessageText] = useState("");
  const [keepAgentText, setKeepAgentText] = useState(true);
  const [textManuallyEdited, setTextManuallyEdited] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [quickReplies, setQuickReplies] = useState([]);
  const [qrLoading, setQrLoading] = useState(false);
  // Bumped when the condition code→label map arrives (old payloads store numeric codes)
  const [condVersion, setCondVersion] = useState(0);
  const [fullTask, setFullTask] = useState(null);
  const [fullTaskLoading, setFullTaskLoading] = useState(false);
  const [userMap, setUserMap] = useState({});

  const isCustomised = !!payload.message_customised;
  const rawType = payload.customer_type ?? null;
  const isB2B = rawType === 0 || rawType === "0";
  const isB2C = rawType === 1 || rawType === "1";

  useEffect(() => {
    if (!open || !payload.parts) return;
    loadConditionLabels().then(() => setCondVersion(v => v + 1));
    const sp = {};
    const vat = {};
    const included = new Set();
    (payload.parts ?? []).forEach(p => {
      sp[p.query_part_id] = p.sp;
      // vat_mode is the current field; fall back to the old boolean for
      // approval requests submitted before the mode dropdown existed.
      vat[p.query_part_id] = { pct: p.vat_pct ?? 5, mode: p.vat_mode ?? (p.vat_enabled ? "incl" : "none") };
      if (p.included) included.add(p.query_part_id);
    });
    setPartSp(sp);
    setPartVat(vat);
    setIncludedPartIds(included);
    setDeliveryEnabled(payload.delivery_enabled ?? true);
    setDeliveryAmount(payload.delivery_amount ?? 0);
    setActiveTab(payload.template_type ?? "b2c");
    setMessageText(payload.message_text ?? "");
    setKeepAgentText(true);
    setTextManuallyEdited(false);
    setRejectMode(false);
    setRejectReason("");
    setApproving(false);
    setRejecting(false);
    setIsDirty(false);
    if (quickReplies.length) return;
    setQrLoading(true);
    _fetchQuickReplies(userId).then(list => {
      setQuickReplies(list.filter(qr => !qr.type || qr.type === "text"));
      setQrLoading(false);
    });
  }, [open]); // eslint-disable-line

  // Full query context (vehicle, customer, timeline) for the admin to
  // reference before approving — this isn't part of the approval payload
  // itself, so it's fetched separately from the same endpoint the task
  // drawer uses.
  useEffect(() => {
    if (!open || !payload.query_pk) { setFullTask(null); return; }
    setFullTaskLoading(true);
    axios
      .get(`${API_DOMAIN}/query_workflow/task/get?user_id=${userId}&query_pk=${payload.query_pk}`, { headers })
      .then(res => { if (res.data.success) setFullTask(res.data.data); })
      .catch(() => {})
      .finally(() => setFullTaskLoading(false));
  }, [open, payload.query_pk]); // eslint-disable-line

  useEffect(() => {
    if (!open) return;
    _fetchSRMUsers().then(list => {
      const map = {};
      list.forEach(u => { map[u.id] = u.fullName; });
      setUserMap(map);
    });
  }, [open]);

  const syntheticCard = useMemo(() => ({
    query_id: payload.query_id,
    customer_name: payload.customer_name,
    customer: { customer_name: payload.customer_name },
  }), [payload.query_id, payload.customer_name]);

  const selectedPartsForInjection = useMemo(() =>
    (payload.parts ?? [])
      .filter(p => includedPartIds.has(p.query_part_id))
      .map(p => ({
        part: { part_name: p.part_name, condition_preference: p.condition },
        quote: { warranty_days: p.warranty_days, quoted_price: p.cp },
        sp: partSp[p.query_part_id] ?? p.sp,
        vatPct: partVat[p.query_part_id]?.pct ?? p.vat_pct ?? 5,
        vatMode: partVat[p.query_part_id]?.mode ?? p.vat_mode ?? (p.vat_enabled ? "incl" : "none"),
      })),
    [payload.parts, includedPartIds, partSp, partVat]
  );

  const deliveryPerPart = deliveryEnabled && selectedPartsForInjection.length > 0
    ? (deliveryAmount || 0) / selectedPartsForInjection.length
    : 0;

  const getTemplate = (tab) => {
    const id = tab === "b2b" ? QR_IDS.b2b : QR_IDS.b2c;
    return quickReplies.find(qr => qr.id === id || qr._id === id);
  };

  // Re-inject template text when values change, unless keeping agent's text or admin edited directly
  useEffect(() => {
    if (!open) return;
    if (isCustomised && keepAgentText) return;
    if (textManuallyEdited) return;
    const qr = getTemplate(activeTab);
    if (!qr || quickReplies.length === 0) return;
    const base = substituteQRText(qr.text || "", authState);
    setMessageText(injectPartsTemplate(base, syntheticCard, selectedPartsForInjection, deliveryPerPart));
  }, [open, activeTab, selectedPartsForInjection, quickReplies, deliveryPerPart, keepAgentText, condVersion]); // eslint-disable-line

  const markDirty = () => setIsDirty(true);

  const togglePart = (qpId) => {
    setIncludedPartIds(prev => {
      const next = new Set(prev);
      if (next.has(qpId)) next.delete(qpId); else next.add(qpId);
      return next;
    });
    markDirty();
  };

  const updateVat = (qpId, key, val) => {
    setPartVat(prev => ({ ...prev, [qpId]: { ...(prev[qpId] ?? { pct: 5, mode: "excl" }), [key]: val } }));
    markDirty();
  };

  const buildModifiedPayload = () => {
    const parts = (payload.parts ?? []).map(p => {
      const isIncluded = includedPartIds.has(p.query_part_id);
      const sp = partSp[p.query_part_id] ?? p.sp;
      const { pct = 5, mode = "excl" } = partVat[p.query_part_id] ?? {};
      const share = isIncluded ? deliveryPerPart : 0;
      const finalPrice = Math.round(mode === "incl" ? (sp + share) * (1 + pct / 100) : (sp + share));
      return {
        ...p,
        included: isIncluded,
        sp,
        vat_pct: pct,
        vat_enabled: mode !== "none", // kept for older readers of this payload shape
        vat_mode: mode,
        delivery_share: isIncluded ? Number(deliveryPerPart.toFixed(2)) : 0,
        final_price: finalPrice,
      };
    });
    return {
      ...payload,
      delivery_enabled: deliveryEnabled,
      delivery_amount: deliveryEnabled ? (deliveryAmount ?? 0) : 0,
      message_text: messageText.trim(),
      parts,
    };
  };

  const handleApprove = async () => {
    if (selectedPartsForInjection.length === 0) {
      antMessage.warning("At least one part must be included before approving");
      return;
    }
    setApproving(true);
    try {
      await axios.post(
        `${API_DOMAIN}/queries/approvals/${approval.id}/approve`,
        { userId, modified_payload: isDirty ? buildModifiedPayload() : null },
        { headers }
      );
      antMessage.success("Quote approved and sent to customer");
      onDone?.("approved", approval.id);
      onClose();
    } catch (e) {
      antMessage.error(e?.response?.data?.message || "Failed to approve");
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      antMessage.warning("Please enter a reason for rejection");
      return;
    }
    setRejecting(true);
    try {
      await axios.post(
        `${API_DOMAIN}/queries/approvals/${approval.id}/reject`,
        { userId, reason: rejectReason.trim() },
        { headers }
      );
      antMessage.info("Quote rejected — agent has been notified");
      onDone?.("rejected", approval.id);
      onClose();
    } catch (e) {
      antMessage.error(e?.response?.data?.message || "Failed to reject");
    } finally {
      setRejecting(false);
    }
  };

  if (!approval) return null;

  const contextCol = (
    <div style={{ overflowY: "auto", paddingRight: 4, borderRight: `1px solid ${t.border}` }}>
      <div style={{ fontWeight: 600, fontSize: 12, color: t.textSub, marginBottom: 8 }}>Query Details</div>
      {fullTaskLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 24 }}><Spin size="small" /></div>
      ) : fullTask?.task ? (
        <OverviewTab task={fullTask.task} afterSales={fullTask.after_sales} parts={fullTask.parts ?? []} userMap={userMap} />
      ) : (
        <div style={{ fontSize: 12, color: t.textSub }}>No additional query details available.</div>
      )}
    </div>
  );

  const leftCol = (
    <div style={{ overflowY: "auto", display: "flex", flexDirection: "column", gap: 8, paddingRight: 4 }}>
      {/* Customer info */}
      <div style={{ padding: "8px 10px", background: t.hover, borderRadius: 6, fontSize: 12, flexShrink: 0 }}>
        <div style={{ color: t.textSub, marginBottom: 6 }}>
          <strong style={{ color: t.text }}>Query:</strong> #{payload.query_id}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <Avatar
            size={32}
            icon={isB2B ? <ShopOutlined /> : isB2C ? <UserOutlined /> : <QuestionCircleOutlined />}
            style={{ backgroundColor: isB2B ? "#1677ff" : isB2C ? "#722ed1" : "#d48806", flexShrink: 0 }}
          />
          <div>
            <div style={{ fontWeight: 600, color: t.text }}>{payload.customer_name || "—"}</div>
            <div style={{ color: t.textSub, fontSize: 11 }}>
              {isB2B ? "B2B · Garage" : isB2C ? "B2C · Retail" : "Type unknown"}
            </div>
          </div>
        </div>
        <div style={{ fontSize: 11, color: t.textSub }}>
          Submitted by{" "}
          <strong style={{ color: t.text }}>{payload.submitted_by_name || "—"}</strong>
          {approval.submitted_at && (() => {
            const d = dayjs.utc(approval.submitted_at.replace(" ", "T"));
            return ` · ${d.format("DD MMM · hh:mm A")} · ${d.from(dayjs.utc().add(4, "hour"))}`;
          })()}
        </div>
        {approval.resubmission_reason && (
          <Alert
            type="warning"
            showIcon
            icon={<WarningOutlined />}
            style={{ marginTop: 8, fontSize: 12 }}
            message={<><strong>Resubmission:</strong> {approval.resubmission_reason}</>}
          />
        )}
      </div>

      {/* Delivery */}
      <div style={{
        border: "1px solid", borderRadius: 6, padding: "8px 10px", flexShrink: 0,
        background: deliveryEnabled ? (isDark ? "rgba(24,144,255,0.12)" : "#e6f7ff") : undefined,
        borderColor: deliveryEnabled ? "#91caff" : t.border,
        transition: "background 0.15s, border-color 0.15s",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: deliveryEnabled ? 6 : 0 }}>
          <Checkbox checked={deliveryEnabled} onChange={e => { setDeliveryEnabled(e.target.checked); markDirty(); }}>
            <span style={{ fontWeight: 600, fontSize: 12 }}>Delivery charge</span>
          </Checkbox>
        </div>
        {deliveryEnabled && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: 24 }}>
            <InputNumber
              value={deliveryAmount}
              min={0}
              size="small"
              prefix="AED"
              style={{ width: 110 }}
              onChange={v => { setDeliveryAmount(v ?? 0); markDirty(); }}
            />
            {selectedPartsForInjection.length > 1 && (
              <span style={{ fontSize: 11, color: t.textSub }}>
                ÷ {selectedPartsForInjection.length} = <strong>AED {(deliveryAmount / selectedPartsForInjection.length).toFixed(2)}/part</strong>
              </span>
            )}
          </div>
        )}
      </div>

      <div style={{ fontWeight: 600, fontSize: 12, color: t.textSub, flexShrink: 0 }}>Parts</div>

      {(payload.parts ?? []).map(p => {
        const checked = includedPartIds.has(p.query_part_id);
        const { pct = 5, mode = "excl" } = partVat[p.query_part_id] ?? {};
        const sp = partSp[p.query_part_id] ?? p.sp;
        const cp = p.cp ?? 0;
        const gp = gpFromSp(cp, sp);
        const gpColor = gp > 0 ? "#389e0d" : "#cf1322";
        const share = checked ? deliveryPerPart : 0;
        const finalPrice = Math.round(mode === "incl" ? (sp + share) * (1 + pct / 100) : (sp + share));
        const vatSuffix = mode === "incl" ? `(Incl. VAT ${pct}%)` : mode === "excl" ? `+ VAT ${pct}%` : "";

        return (
          <div
            key={p.query_part_id}
            style={{
              border: `1px solid ${checked ? "#b7eb8f" : t.border}`,
              borderRadius: 6, padding: "8px 10px",
              background: checked ? (isDark ? "rgba(82,196,26,0.12)" : "#f6ffed") : undefined, flexShrink: 0,
              transition: "background 0.15s, border-color 0.15s",
            }}
          >
            <div
              style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
              onClick={() => togglePart(p.query_part_id)}
            >
              <Checkbox checked={checked} onChange={() => {}} />
              <span style={{ fontWeight: 600, fontSize: 12, flex: 1 }}>{p.part_name}</span>
            </div>
            <div style={{ marginTop: 6, marginLeft: 24, fontSize: 11, color: t.textSub, lineHeight: 1.7 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }} onClick={e => e.stopPropagation()}>
                <InputNumber
                  value={sp}
                  min={0}
                  size="small"
                  prefix="AED"
                  style={{ width: 110, fontWeight: 600 }}
                  onChange={v => { setPartSp(prev => ({ ...prev, [p.query_part_id]: v ?? 0 })); markDirty(); }}
                />
                <Tooltip title={Number(p.warranty_days || 0) > 0 ? `${p.warranty_days} days replacement warranty` : "No warranty"}>
                  <span style={{ color: t.textSub, cursor: "default" }}>
                    {p.condition || "New"}
                    {Number(p.warranty_days || 0) > 0 ? ` · ${p.warranty_days}d` : " · No warranty"}
                  </span>
                </Tooltip>
              </div>
              <div style={{ fontSize: 11, color: t.textSub, marginTop: 2, display: "flex", alignItems: "center", gap: 6 }}>
                <Tooltip title="Cost price — what the supplier quoted">
                  <span style={{ cursor: "default" }}>
                    CP: <strong style={{ color: t.text }}>AED {cp.toLocaleString()}</strong>
                  </span>
                </Tooltip>
                {" · "}
                <Tooltip title="Gross profit margin based on current selling price">
                  <span style={{ cursor: "default" }}>
                    GP: <strong style={{ color: gpColor }}>{gp.toFixed(1)}%</strong>
                  </span>
                </Tooltip>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4, flexWrap: "wrap" }} onClick={e => e.stopPropagation()}>
                <Select
                  value={mode}
                  size="small"
                  style={{ width: 128 }}
                  onChange={v => updateVat(p.query_part_id, "mode", v)}
                  options={[
                    { value: "incl", label: "Including VAT" },
                    { value: "excl", label: "Excluding VAT" },
                    { value: "none", label: "No VAT" },
                  ]}
                />
                <InputNumber
                  value={pct} min={0} max={100} size="small" disabled={mode === "none"} suffix="%"
                  style={{ width: 70 }}
                  onChange={v => updateVat(p.query_part_id, "pct", v ?? 0)}
                />
                <span style={{ fontSize: 11, color: t.textSub, display: "flex", alignItems: "center", gap: 4 }}>
                  {checked && share > 0 && (
                    <Tooltip title={`Delivery share (${deliveryAmount} ÷ ${selectedPartsForInjection.length} part${selectedPartsForInjection.length > 1 ? "s" : ""})`}>
                      <span style={{ color: "#1677ff", cursor: "default" }}>
                        +{share % 1 === 0 ? share : share.toFixed(2)} del
                      </span>
                    </Tooltip>
                  )}
                  <Tooltip title="Final customer-facing price after SP + delivery + VAT">
                    <span style={{
                      color: "#389e0d", fontWeight: 700, fontSize: 13,
                      background: isDark ? "rgba(82,196,26,0.12)" : "#f6ffed", border: "1px solid #b7eb8f",
                      borderRadius: 4, padding: "1px 6px", cursor: "default",
                    }}>
                      AED {finalPrice.toLocaleString()}
                    </span>
                  </Tooltip>
                  {vatSuffix && <span style={{ color: t.textSub }}>{vatSuffix}</span>}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const rightCol = (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, minHeight: 0 }}>
      {isCustomised && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ flexShrink: 0, fontSize: 11 }}
          message={
            <div>
              <strong>Agent customised this message</strong> — the text was manually edited before submission.
              <div style={{ marginTop: 6, display: "flex", gap: 8 }}>
                <Button
                  size="small"
                  type={keepAgentText ? "primary" : "default"}
                  onClick={() => { setKeepAgentText(true); setTextManuallyEdited(false); setMessageText(payload.message_text ?? ""); }}
                >
                  Keep agent's text
                </Button>
                <Button
                  size="small"
                  type={!keepAgentText ? "primary" : "default"}
                  icon={<ReloadOutlined />}
                  loading={qrLoading}
                  onClick={() => { setKeepAgentText(false); setTextManuallyEdited(false); }}
                >
                  Use fresh template
                </Button>
              </div>
            </div>
          }
        />
      )}

      {!isCustomised && (
        <Tabs
          activeKey={activeTab}
          onChange={v => { setActiveTab(v); setTextManuallyEdited(false); markDirty(); }}
          size="small"
          style={{ flexShrink: 0 }}
          items={[
            { key: "b2c", label: "B2C · Retail" },
            { key: "b2b", label: "B2B · Garage" },
          ]}
        />
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        {textManuallyEdited && (
          <>
            <Tag color="orange" style={{ fontSize: 11, margin: 0 }}>Manually edited</Tag>
            <Tooltip title="Discard your edits and restore auto-generated text">
              <Button
                size="small" type="text"
                style={{ fontSize: 11, color: t.textSub, padding: "0 4px" }}
                onClick={() => { setTextManuallyEdited(false); }}
              >
                Reset to template
              </Button>
            </Tooltip>
          </>
        )}
      </div>

      {qrLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 32 }}><Spin /></div>
      ) : (
        <Input.TextArea
          value={messageText}
          onChange={e => { setMessageText(e.target.value); setTextManuallyEdited(true); markDirty(); }}
          style={{
            fontSize: 12, fontFamily: "inherit", flex: 1, resize: "none", overflowY: "auto",
          }}
          placeholder="Message preview…"
        />
      )}
    </div>
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <CheckCircleOutlined style={{ color: "#faad14" }} />
          Review quote — #{payload.query_id}
          {isDirty && <Tag color="orange" style={{ fontSize: 10, margin: 0 }}>Modified</Tag>}
        </div>
      }
      style={{ top: 16, paddingBottom: 0 }}
      styles={{
        content: { height: "calc(100vh - 32px)", display: "flex", flexDirection: "column", padding: 0, overflow: "hidden" },
        header: { padding: "16px 24px", flexShrink: 0 },
        body: { flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column", padding: "0 24px" },
        footer: { padding: "12px 24px", flexShrink: 0 },
      }}
      footer={
        rejectMode ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Input.TextArea
              rows={2}
              placeholder="Reason for rejection (required)…"
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              autoFocus
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <Button onClick={() => { setRejectMode(false); setRejectReason(""); }}>Cancel</Button>
              <Button
                danger type="primary"
                icon={<CloseCircleOutlined />}
                loading={rejecting}
                onClick={handleReject}
              >
                Confirm Rejection
              </Button>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
            <Button danger icon={<CloseCircleOutlined />} onClick={() => setRejectMode(true)}>
              Reject
            </Button>
            <div style={{ display: "flex", gap: 8 }}>
              <Button onClick={onClose}>Cancel</Button>
              <Button
                type="primary"
                icon={<SendOutlined />}
                loading={approving}
                disabled={!messageText.trim() || selectedPartsForInjection.length === 0}
                onClick={handleApprove}
              >
                Approve &amp; Send{isDirty ? " (modified)" : ""}
              </Button>
            </div>
          </div>
        )
      }
      width={1180}
      destroyOnHidden
      zIndex={1060}
    >
      <div style={{ display: "grid", gridTemplateColumns: "260px 300px 1fr", gap: 16, flex: 1, minHeight: 0 }}>
        {contextCol}
        {leftCol}
        {rightCol}
      </div>
    </Modal>
  );
}
